import { useCallback, useEffect, useRef, useState } from 'react';

import { trackEvent } from '@/modules/analytics';
import { submitPreDiabetesResponses } from '@/modules/api';
import {
  AssessmentContent,
  toSingleAnswerValue,
  type Answers,
  type Conditions,
} from '@/modules/dynamicAssessments';
import { useOnboardingSteps, useRulesEngine } from '@/modules/rulesEngine';
import { LoadingScreen } from '@/modules/ui';

import { usePreDiabetesQuestions } from './hooks/usePreDiabetesQuestions';

// Branching: the first question (pre-diabetes-0, "sex at birth") skips the
// pregnancy question (order 1) and jumps to order 2 when "Male" (choice 0) is
// selected — Sparta's PreDiabetesScreenContainer condition. That's the
// 4-vs-5-question difference.
const CONDITIONS: Conditions = {
  'pre-diabetes-0': { jumpToIf: { choice: 0, nextQuestion: 2 } },
};

/**
 * Pre-diabetes risk screener — part of the dynamic assessment region, after
 * assigned screeners. Reuses the shared AssessmentContent renderer. Submitting
 * records the `prediabetes-risk` metric point on the backend. A load error or
 * empty question set skips ahead (matches the assessment's fail-forward behavior).
 */
export function PreDiabetesScreen() {
  const { user } = useRulesEngine();
  const { goToNextStep } = useOnboardingSteps();
  const { questions, isLoading, error } = usePreDiabetesQuestions();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const viewedRef = useRef(false);

  const advance = useCallback(
    () => goToNextStep({ from: '/step/pre-diabetes-screen' }),
    [goToNextStep]
  );

  const onContinue = useCallback(
    async (answers: Answers) => {
      const userUrn = user?.urn;
      if (!userUrn) {
        advance();
        return;
      }
      trackEvent('pre-diabetes-screen-click-continue', { uuid: user?.uuid });
      try {
        setIsSubmitting(true);
        await submitPreDiabetesResponses(
          userUrn,
          Object.entries(answers).map(([questionKey, value]) => ({
            questionKey,
            surveyChoiceUrn: toSingleAnswerValue(value),
          }))
        );
        advance();
      } catch {
        advance();
      } finally {
        setIsSubmitting(false);
      }
    },
    [user?.urn, user?.uuid, advance]
  );

  const shouldAdvance = !isLoading && (error != null || questions.length === 0);
  useEffect(() => {
    if (shouldAdvance) advance();
  }, [shouldAdvance, advance]);

  // Fire "viewed" once the screen actually shows its questions (not while
  // loading or fail-forwarding past it).
  useEffect(() => {
    if (!isLoading && !shouldAdvance && user?.uuid && !viewedRef.current) {
      viewedRef.current = true;
      trackEvent('pre-diabetes-screen-viewed', { uuid: user.uuid });
    }
  }, [isLoading, shouldAdvance, user?.uuid]);

  if (isLoading || shouldAdvance) {
    return <LoadingScreen />;
  }

  return (
    <AssessmentContent
      questions={questions}
      conditions={CONDITIONS}
      onContinue={onContinue}
      isSubmitting={isSubmitting}
      testIDPrefix='pre-diabetes'
    />
  );
}
